import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import type { LucideIcon } from "lucide-react";
import React from "react";

export interface WorkspaceHeaderButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  pressed?: boolean;
  /** Where the tooltip flies out — above by default, the canvas toolbar's
   *  register; Radix flips it when the viewport edge is too close. */
  tooltipSide?: "top" | "bottom" | "left" | "right";
}

/** Shared header glyph button — size-8 (32px), rounded-md, rust focus ring.
 *  Carries the house tooltip (same register as the canvas toolbar buttons):
 *  instant, labeled with `label`, so icon-only glyphs always name
 *  themselves. */
export const WorkspaceHeaderButton = React.forwardRef<
  HTMLButtonElement,
  WorkspaceHeaderButtonProps
>(function WorkspaceHeaderButton(
  {
    icon: Icon,
    label,
    pressed,
    onClick,
    className,
    children,
    tooltipSide = "top",
    ...props
  },
  ref,
) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          aria-pressed={pressed}
          aria-label={label}
          className={cn(
            "inline-flex size-8 shrink-0 items-center justify-center rounded-md text-n-50 outline-none transition-colors hover:bg-n-05 hover:text-n-80 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]",
            pressed && "bg-n-10 text-n-90 hover:bg-n-10 hover:text-n-90",
            className,
          )}
          {...props}
        >
          {children ?? <Icon className="size-4" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{label}</TooltipContent>
    </Tooltip>
  );
});
